/**
 * Configuração pública da instalação (CERES-EST-001 §11).
 *
 * O que a tela precisa saber ANTES de existir sessão. Uma fonte só: a interface
 * não guarda bandeira própria, ela pergunta ao backend — que é quem decide e
 * quem recusa. Duas bandeiras independentes é como se chega ao estado em que o
 * botão aparece e a API responde 403.
 *
 * O padrão enquanto a resposta não chega (e se ela falhar) é DESABILITADO: um
 * erro de rede não pode abrir uma porta que a instalação mantém fechada. O
 * preço é um botão que demora um instante para aparecer onde ele existe; a
 * alternativa seria mostrá-lo onde ele não funciona.
 */

import { useEffect, useState } from 'react';
import { api } from './api';

export interface ConfiguracaoPublica {
  cadastroPublicoHabilitado: boolean;
}

const PADRAO_SEGURO: ConfiguracaoPublica = { cadastroPublicoHabilitado: false };

/** Memória de processo: a configuração não muda sem reiniciar a API. */
let cache: ConfiguracaoPublica | null = null;

/** Somente para testes: descarta a memória entre cenários. */
export function limparCacheDaConfiguracaoPublica(): void {
  cache = null;
}

export function useConfiguracaoPublica(): { config: ConfiguracaoPublica; carregando: boolean } {
  const [config, setConfig] = useState<ConfiguracaoPublica>(cache ?? PADRAO_SEGURO);
  const [carregando, setCarregando] = useState(cache === null);

  useEffect(() => {
    if (cache !== null) return;
    let vivo = true;

    /*
     * `await` dentro de try/catch, e não uma cadeia de `.then`: assim a
     * consulta pode falhar de QUALQUER jeito — rejeitar, lançar de forma
     * síncrona, ou nem devolver uma promessa — sem derrubar a tela de login.
     * Uma consulta acessória não pode ser o motivo de ninguém conseguir entrar.
     */
    void (async () => {
      try {
        const resposta = await api.get<ConfiguracaoPublica>('/auth/configuracao-publica');
        if (resposta !== null && typeof resposta === 'object') {
          cache = resposta;
          if (vivo) setConfig(resposta);
        }
      } catch {
        // Silencioso de propósito: o padrão seguro já está aplicado.
      } finally {
        if (vivo) setCarregando(false);
      }
    })();

    return () => {
      vivo = false;
    };
  }, []);

  return { config, carregando };
}
